<script setup lang="ts">
import { ref, computed, watch } from "vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Bar } from "vue-chartjs";
import type { PostTestRecord } from "@/services/postTestService";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// PreTest interface - knowledge-based assessment
interface PreTestRecord {
  sessionId: string;
  userId: string;
  subject: string;
  topic: string;
  date_completed: any; // ✅ FIX: Use date_completed to match Firebase field name
  score: number; // Number of correct answers
  totalQuestions: number;
  percentage: number; // Score as percentage (0-100)
  questions: any[];
  language: string;
  sources: string[];
}

interface Props {
  period: "day" | "week" | "month" | "year";
  postTests?: PostTestRecord[]; // Accept real post-test data
  preTests?: PreTestRecord[]; // NEW: Accept real pre-test data
}

const props = withDefaults(defineProps<Props>(), {
  postTests: () => [],
  preTests: () => [],
});

// Helper function to process pre-tests
const processPreTestData = computed(() => {
  if (!props.preTests || props.preTests.length === 0) {
    return {
      labels: [],
      scores: [],
      testDates: [],
      testSubjects: [],
    };
  }

  // Sort tests chronologically (oldest first for processing, will limit to recent)
  const sortedTests = [...props.preTests].sort((a, b) => {
    const dateA = a.date_completed?.toDate?.().getTime() || 0;
    const dateB = b.date_completed?.toDate?.().getTime() || 0;
    return dateA - dateB;
  });

  // ✅ NEW: Limit to 10 most recent pre-tests to prevent overcrowding
  const limitedTests = sortedTests.slice(-10);

  const labels: string[] = [];
  const scores: number[] = [];
  const testDates: Date[] = [];
  const testSubjects: string[] = [];
  const testCounts: number[] = []; // Track how many tests were aggregated

  // For pre-tests, we'll use the same logic as post-tests
  // Process similarly to post-tests based on period
  switch (props.period) {
    case "day": {
      // ✅ IMPROVED: Aggregate tests by day (no time) to prevent overcrowding
      // Format: "Nov 11 (Mon)"
      const dayAggregates = new Map<
        string,
        { scores: number[]; subjects: Set<string>; date: Date }
      >();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();

        // ✅ FIX: Normalize to start of day in local timezone
        const normalizedDate = new Date(
          testDate.getFullYear(),
          testDate.getMonth(),
          testDate.getDate(),
          0,
          0,
          0,
          0
        );
        const dateKey = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}-${normalizedDate.getDate()}`;

        if (!dayAggregates.has(dateKey)) {
          dayAggregates.set(dateKey, {
            scores: [],
            subjects: new Set(),
            date: normalizedDate, // Use normalized date
          });
        }

        const aggregate = dayAggregates.get(dateKey)!;
        aggregate.scores.push(test.percentage || 0);
        aggregate.subjects.add(test.subject || "Unknown");
      });

      // Sort by date and limit to last 7 days
      const sortedDays = Array.from(dayAggregates.entries())
        .sort((a, b) => a[1].date.getTime() - b[1].date.getTime())
        .slice(-7);

      sortedDays.forEach(([dateKey, aggregate]) => {
        const avgScore = Math.round(
          aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
        );

        // Format: "Nov 11 (Mon)"
        const dateStr = aggregate.date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const dayStr = aggregate.date.toLocaleDateString("en-US", {
          weekday: "short",
        });
        const label = `${dateStr} (${dayStr})`;

        labels.push(label);
        scores.push(avgScore);
        testDates.push(aggregate.date);
        testSubjects.push(Array.from(aggregate.subjects).join(", "));
        testCounts.push(aggregate.scores.length);
      });
      break;
    }

    case "week": {
      // ✅ FIXED: Show weeks based on Sunday-Saturday calendar weeks
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Get first and last day of current month
      const monthStart = new Date(currentYear, currentMonth, 1);
      const monthEnd = new Date(currentYear, currentMonth + 1, 0);

      // Find all Sundays in the current month
      const sundays: Date[] = [];
      for (let day = 1; day <= monthEnd.getDate(); day++) {
        const date = new Date(currentYear, currentMonth, day);
        if (date.getDay() === 0) {
          // Sunday
          sundays.push(date);
        }
      }

      // If month doesn't start on Sunday, include the Sunday before month start
      if (monthStart.getDay() !== 0) {
        const firstSunday = new Date(monthStart);
        firstSunday.setDate(firstSunday.getDate() - firstSunday.getDay());
        if (
          firstSunday.getMonth() === currentMonth - 1 ||
          (currentMonth === 0 && firstSunday.getMonth() === 11)
        ) {
          sundays.unshift(firstSunday);
        }
      }

      // Create week ranges (Sunday to Saturday)
      const weekRanges: Array<{ start: Date; end: Date; weekNum: number }> = [];
      sundays.forEach((sunday, index) => {
        const saturday = new Date(sunday);
        saturday.setDate(saturday.getDate() + 6);

        // Only include weeks that overlap with current month
        if (sunday.getMonth() === currentMonth || saturday.getMonth() === currentMonth) {
          weekRanges.push({
            start: sunday,
            end: saturday,
            weekNum: index + 1,
          });
        }
      });

      // Map to store aggregated scores by week
      const weekAggregates = new Map<
        number,
        { scores: number[]; subjects: Set<string>; start: Date; end: Date }
      >();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();

        // Find which week this test belongs to
        for (const week of weekRanges) {
          if (testDate >= week.start && testDate <= week.end) {
            if (!weekAggregates.has(week.weekNum)) {
              weekAggregates.set(week.weekNum, {
                scores: [],
                subjects: new Set(),
                start: week.start,
                end: week.end,
              });
            }
            const aggregate = weekAggregates.get(week.weekNum)!;
            aggregate.scores.push(test.percentage || 0);
            aggregate.subjects.add(test.subject || "Unknown");
            break;
          }
        }
      });

      // Generate labels for all weeks in the month
      weekRanges.forEach((week) => {
        const aggregate = weekAggregates.get(week.weekNum);

        // Format date range (e.g., "Week 3: Dec 14-20")
        const startDay = week.start.getDate();
        const endDay = week.end.getDate();
        const monthName = week.start.toLocaleDateString("en-US", { month: "short" });
        const label = `Week ${week.weekNum}: ${monthName} ${startDay}-${endDay}`;

        labels.push(label);
        testDates.push(week.start);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      });
      break;
    }

    case "month": {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const monthAggregates = new Map<string, { scores: number[]; subjects: Set<string> }>();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
        const testMonth = testDate.getMonth();
        const testYear = testDate.getFullYear();

        const monthDiff = (currentYear - testYear) * 12 + (currentMonth - testMonth);

        if (monthDiff >= 0 && monthDiff <= 2) {
          const monthKey = `${testYear}-${testMonth}`;

          if (!monthAggregates.has(monthKey)) {
            monthAggregates.set(monthKey, { scores: [], subjects: new Set() });
          }
          const aggregate = monthAggregates.get(monthKey)!;
          aggregate.scores.push(test.percentage || 0);
          aggregate.subjects.add(test.subject || "Unknown");
        }
      });

      // ✅ FIXED: Always show all 3 months on x-axis, even if no data
      for (let i = 2; i >= 0; i--) {
        const targetDate = new Date(currentYear, currentMonth - i, 1);
        const monthKey = `${targetDate.getFullYear()}-${targetDate.getMonth()}`;
        const aggregate = monthAggregates.get(monthKey);

        // ✅ Show month name (e.g., "Sep", "Oct", "Nov")
        const label = targetDate.toLocaleDateString("en-US", {
          month: "short",
        });
        labels.push(label);
        testDates.push(targetDate);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          // No data for this month - push null
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      }
      break;
    }

    case "year": {
      const now = new Date();
      const currentYear = now.getFullYear();
      const monthAggregates = new Map<number, { scores: number[]; subjects: Set<string> }>();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
        const testYear = testDate.getFullYear();
        const testMonth = testDate.getMonth();

        if (testYear === currentYear) {
          if (!monthAggregates.has(testMonth)) {
            monthAggregates.set(testMonth, { scores: [], subjects: new Set() });
          }
          const aggregate = monthAggregates.get(testMonth)!;
          aggregate.scores.push(test.percentage || 0);
          aggregate.subjects.add(test.subject || "Unknown");
        }
      });

      // ✅ FIXED: Always show all 12 months on x-axis, even if no data
      for (let month = 0; month < 12; month++) {
        const aggregate = monthAggregates.get(month);
        const monthDate = new Date(currentYear, month, 1);
        const label = monthDate.toLocaleDateString("en-US", {
          month: "short",
        });

        labels.push(label);
        testDates.push(monthDate);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          // No data for this month - push null
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      }
      break;
    }
  }

  return { labels, scores, testDates, testSubjects, testCounts };
});

// Helper function to process post-tests - aggregates by period for week/month/year views
const processTestData = computed(() => {
  if (!props.postTests || props.postTests.length === 0) {
    return {
      labels: [],
      scores: [],
      testDates: [],
      testSubjects: [],
    };
  }

  // Sort tests chronologically (oldest first for processing, will limit to recent)
  const sortedTests = [...props.postTests].sort((a, b) => {
    const dateA = a.date_completed?.toDate?.().getTime() || 0;
    const dateB = b.date_completed?.toDate?.().getTime() || 0;
    return dateA - dateB;
  });

  // ✅ NEW: Limit to 10 most recent post-tests to prevent overcrowding
  const limitedTests = sortedTests.slice(-10);

  const labels: string[] = [];
  const scores: number[] = [];
  const testDates: Date[] = [];
  const testSubjects: string[] = [];
  const testCounts: number[] = []; // Track how many tests were aggregated

  // ✅ NEW: Different processing based on period
  switch (props.period) {
    case "day": {
      // ✅ IMPROVED: Aggregate tests by day (no time) to prevent overcrowding
      // Format: "Nov 11 (Mon)"
      const dayAggregates = new Map<
        string,
        { scores: number[]; subjects: Set<string>; date: Date }
      >();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();

        // ✅ FIX: Normalize to start of day in local timezone
        const normalizedDate = new Date(
          testDate.getFullYear(),
          testDate.getMonth(),
          testDate.getDate(),
          0,
          0,
          0,
          0
        );
        const dateKey = `${normalizedDate.getFullYear()}-${normalizedDate.getMonth()}-${normalizedDate.getDate()}`;

        if (!dayAggregates.has(dateKey)) {
          dayAggregates.set(dateKey, {
            scores: [],
            subjects: new Set(),
            date: normalizedDate, // Use normalized date
          });
        }

        const aggregate = dayAggregates.get(dateKey)!;
        // ✅ FIX: Use percentage field (0-100) instead of score field (0-10)
        const percentageScore =
          test.percentage !== undefined && test.percentage !== null
            ? test.percentage
            : test.score && test.score <= 10
            ? test.score * 10
            : test.score || 0;
        aggregate.scores.push(percentageScore);
        aggregate.subjects.add(test.subject || "Unknown");
      });

      // Sort by date and limit to last 7 days
      const sortedDays = Array.from(dayAggregates.entries())
        .sort((a, b) => a[1].date.getTime() - b[1].date.getTime())
        .slice(-7);

      sortedDays.forEach(([dateKey, aggregate]) => {
        const avgScore = Math.round(
          aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
        );

        // Format: "Nov 11 (Mon)"
        const dateStr = aggregate.date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
        const dayStr = aggregate.date.toLocaleDateString("en-US", {
          weekday: "short",
        });
        const label = `${dateStr} (${dayStr})`;

        labels.push(label);
        scores.push(avgScore);
        testDates.push(aggregate.date);
        testSubjects.push(Array.from(aggregate.subjects).join(", "));
        testCounts.push(aggregate.scores.length);
      });
      break;
    }

    case "week": {
      // ✅ FIXED: Show weeks based on Sunday-Saturday calendar weeks
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      // Get first and last day of current month
      const monthStart = new Date(currentYear, currentMonth, 1);
      const monthEnd = new Date(currentYear, currentMonth + 1, 0);

      // Find all Sundays in the current month
      const sundays: Date[] = [];
      for (let day = 1; day <= monthEnd.getDate(); day++) {
        const date = new Date(currentYear, currentMonth, day);
        if (date.getDay() === 0) {
          // Sunday
          sundays.push(date);
        }
      }

      // If month doesn't start on Sunday, include the Sunday before month start
      if (monthStart.getDay() !== 0) {
        const firstSunday = new Date(monthStart);
        firstSunday.setDate(firstSunday.getDate() - firstSunday.getDay());
        if (
          firstSunday.getMonth() === currentMonth - 1 ||
          (currentMonth === 0 && firstSunday.getMonth() === 11)
        ) {
          sundays.unshift(firstSunday);
        }
      }

      // Create week ranges (Sunday to Saturday)
      const weekRanges: Array<{ start: Date; end: Date; weekNum: number }> = [];
      sundays.forEach((sunday, index) => {
        const saturday = new Date(sunday);
        saturday.setDate(saturday.getDate() + 6);

        // Only include weeks that overlap with current month
        if (sunday.getMonth() === currentMonth || saturday.getMonth() === currentMonth) {
          weekRanges.push({
            start: sunday,
            end: saturday,
            weekNum: index + 1,
          });
        }
      });

      // Map to store aggregated scores by week
      const weekAggregates = new Map<
        number,
        { scores: number[]; subjects: Set<string>; start: Date; end: Date }
      >();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();

        // Find which week this test belongs to
        for (const week of weekRanges) {
          if (testDate >= week.start && testDate <= week.end) {
            if (!weekAggregates.has(week.weekNum)) {
              weekAggregates.set(week.weekNum, {
                scores: [],
                subjects: new Set(),
                start: week.start,
                end: week.end,
              });
            }
            const aggregate = weekAggregates.get(week.weekNum)!;
            // ✅ FIX: Use percentage field (0-100) instead of score field (0-10)
            const percentageScore =
              test.percentage !== undefined && test.percentage !== null
                ? test.percentage
                : test.score && test.score <= 10
                ? test.score * 10
                : test.score || 0;
            aggregate.scores.push(percentageScore);
            aggregate.subjects.add(test.subject || "Unknown");
            break;
          }
        }
      });

      // Generate labels for all weeks in the month
      weekRanges.forEach((week) => {
        const aggregate = weekAggregates.get(week.weekNum);

        // Format date range (e.g., "Week 3: Dec 14-20")
        const startDay = week.start.getDate();
        const endDay = week.end.getDate();
        const monthName = week.start.toLocaleDateString("en-US", { month: "short" });
        const label = `Week ${week.weekNum}: ${monthName} ${startDay}-${endDay}`;

        labels.push(label);
        testDates.push(week.start);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      });
      break;
    }

    case "month": {
      // ✅ UPDATED: Aggregate by month (show current + 2 previous = 3 months)
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const monthAggregates = new Map<string, { scores: number[]; subjects: Set<string> }>();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
        const testMonth = testDate.getMonth();
        const testYear = testDate.getFullYear();

        // Calculate month difference
        const monthDiff = (currentYear - testYear) * 12 + (currentMonth - testMonth);

        // Only include current month (0) and 2 previous months (1, 2)
        if (monthDiff >= 0 && monthDiff <= 2) {
          const monthKey = `${testYear}-${testMonth}`;

          if (!monthAggregates.has(monthKey)) {
            monthAggregates.set(monthKey, { scores: [], subjects: new Set() });
          }
          const aggregate = monthAggregates.get(monthKey)!;
          // ✅ FIX: Use percentage field (0-100) instead of score field (0-10)
          const percentageScore =
            test.percentage !== undefined && test.percentage !== null
              ? test.percentage
              : test.score && test.score <= 10
              ? test.score * 10
              : test.score || 0;
          aggregate.scores.push(percentageScore);
          aggregate.subjects.add(test.subject || "Unknown");
        }
      });

      // ✅ FIXED: Always show all 3 months on x-axis, even if no data
      for (let i = 2; i >= 0; i--) {
        const targetDate = new Date(currentYear, currentMonth - i, 1);
        const monthKey = `${targetDate.getFullYear()}-${targetDate.getMonth()}`;
        const aggregate = monthAggregates.get(monthKey);

        // ✅ Show month name (e.g., "Sep", "Oct", "Nov")
        const label = targetDate.toLocaleDateString("en-US", {
          month: "short",
        });
        labels.push(label);
        testDates.push(targetDate);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          // No data for this month - push null
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      }
      break;
    }

    case "year": {
      // ✅ UPDATED: Aggregate by month (show all 12 months of current year)
      const now = new Date();
      const currentYear = now.getFullYear();
      const monthAggregates = new Map<number, { scores: number[]; subjects: Set<string> }>();

      limitedTests.forEach((test) => {
        const testDate = test.date_completed?.toDate ? test.date_completed.toDate() : new Date();
        const testYear = testDate.getFullYear();
        const testMonth = testDate.getMonth();

        if (testYear === currentYear) {
          if (!monthAggregates.has(testMonth)) {
            monthAggregates.set(testMonth, { scores: [], subjects: new Set() });
          }
          const aggregate = monthAggregates.get(testMonth)!;
          // ✅ FIX: Use percentage field (0-100) instead of score field (0-10)
          const percentageScore =
            test.percentage !== undefined && test.percentage !== null
              ? test.percentage
              : test.score && test.score <= 10
              ? test.score * 10
              : test.score || 0;
          aggregate.scores.push(percentageScore);
          aggregate.subjects.add(test.subject || "Unknown");
        }
      });

      // ✅ FIXED: Always show all 12 months on x-axis, even if no data
      for (let month = 0; month < 12; month++) {
        const aggregate = monthAggregates.get(month);
        const monthDate = new Date(currentYear, month, 1);
        const label = monthDate.toLocaleDateString("en-US", {
          month: "short",
        });

        labels.push(label);
        testDates.push(monthDate);

        if (aggregate && aggregate.scores.length > 0) {
          const avgScore = Math.round(
            aggregate.scores.reduce((sum, s) => sum + s, 0) / aggregate.scores.length
          );
          scores.push(avgScore);
          testSubjects.push(Array.from(aggregate.subjects).join(", "));
          testCounts.push(aggregate.scores.length);
        } else {
          // No data for this month - push null
          scores.push(null as any);
          testSubjects.push("");
          testCounts.push(0);
        }
      }
      break;
    }
  }

  return { labels, scores, testDates, testSubjects, testCounts };
});

// Chart data computed property with dual-bar support (side-by-side bars)
const chartData = computed((): ChartData<"bar"> => {
  const postData = processTestData.value;
  const preData = processPreTestData.value;

  // ✅ IMPROVED: Create descriptive x-axis labels based on period and actual data
  // Determine the maximum number of points to show
  const maxPoints = Math.max(preData.scores.length, postData.scores.length, 1);

  // If no data from either source, return empty datasets
  if (maxPoints === 0 || (preData.scores.length === 0 && postData.scores.length === 0)) {
    return {
      labels: [],
      datasets: [],
    };
  }

  // ✅ IMPROVED: Create descriptive labels based on period type
  let labels: string[] = [];

  // Use labels from whichever dataset has more data points (prioritize post-test labels)
  if (postData.labels.length >= preData.labels.length) {
    labels = postData.labels;
  } else {
    labels = preData.labels;
  }

  // Fallback: If no labels from either dataset, create generic labels
  if (labels.length === 0) {
    labels = Array.from({ length: maxPoints }, (_, i) => {
      switch (props.period) {
        case "day":
          return `Test ${i + 1}`;
        case "week":
          return `Week ${i + 1}`;
        case "month":
          return `Month ${i + 1}`;
        case "year":
          return `Period ${i + 1}`;
        default:
          return String(i + 1);
      }
    });
  }

  // Ensure labels array matches maxPoints length
  while (labels.length < maxPoints) {
    const nextIndex = labels.length + 1;
    switch (props.period) {
      case "day":
        labels.push(`Test ${nextIndex}`);
        break;
      case "week":
        labels.push(`Week ${nextIndex}`);
        break;
      case "month":
        labels.push(`Month ${nextIndex}`);
        break;
      case "year":
        labels.push(`Period ${nextIndex}`);
        break;
      default:
        labels.push(String(nextIndex));
    }
  }

  const datasets = [];

  // Add pre-test dataset (orange)
  if (preData.scores.length > 0) {
    // Pad with nulls if pre-tests have fewer points than post-tests
    const paddedPreScores = [...preData.scores];
    while (paddedPreScores.length < maxPoints) {
      paddedPreScores.push(null as any);
    }

    datasets.push({
      label: "Pre-Test",
      data: paddedPreScores,
      backgroundColor: "rgba(59, 130, 246, 0.8)", // Blue
      borderColor: "#3B82F6",
      borderWidth: 2,
      borderRadius: 6,
      hoverBackgroundColor: "rgba(59, 130, 246, 1)",
    });
  }

  // Add post-test dataset (green)
  if (postData.scores.length > 0) {
    // Pad with nulls if post-tests have fewer points than pre-tests
    const paddedPostScores = [...postData.scores];
    while (paddedPostScores.length < maxPoints) {
      paddedPostScores.push(null as any);
    }

    datasets.push({
      label: "Post-Test",
      data: paddedPostScores,
      backgroundColor: "rgba(147, 51, 234, 0.8)", // Purple
      borderColor: "#9333EA",
      borderWidth: 2,
      borderRadius: 6,
      hoverBackgroundColor: "rgba(147, 51, 234, 1)",
    });
  }

  return {
    labels,
    datasets,
  };
});

// Chart options
const chartOptions = computed(
  (): ChartOptions<"bar"> => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true, // NEW: Show legend for dual-line chart
        position: "top",
        align: "end",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 15,
          font: {
            size: 12,
            weight: 500,
          },
          color: "#374151",
        },
      },
      tooltip: {
        backgroundColor: "#1F2937",
        titleColor: "#FFFFFF",
        bodyColor: "#FFFFFF",
        borderColor: "#374151",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        displayColors: true, // NEW: Show color indicators for datasets
        callbacks: {
          title: (context) => {
            // ✅ FIX: Use the x-axis label directly for consistency
            // This ensures tooltip date matches what user sees on chart
            return context[0].label;
          },
          label: (context) => {
            const datasetLabel = context.dataset.label || "";
            const score = context.parsed.y;
            const index = context.dataIndex;

            if (score === null || score === undefined) {
              return ""; // Return empty string for null values
            }

            const postData = processTestData.value;
            const preData = processPreTestData.value;

            let subject = "Unknown";
            let testCount = 1;

            if (datasetLabel === "Pre-Test") {
              subject = preData.testSubjects?.[index] || "Unknown";
              testCount = preData.testCounts?.[index] || 1;
            } else {
              subject = postData.testSubjects?.[index] || "Unknown";
              testCount = postData.testCounts?.[index] || 1;
            }

            // ✅ Show average score with test count for aggregated periods
            const result = [];

            if (testCount > 1) {
              result.push(`${datasetLabel}: ${score}% (avg)`);
              result.push(`Tests: ${testCount}`);
            } else {
              result.push(`${datasetLabel}: ${score}%`);
            }

            result.push(`Subject${testCount > 1 ? "s" : ""}: ${subject}`);

            return result;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "#F3F4F6",
          lineWidth: 1,
        },
        border: {
          display: true,
          color: "#E5E7EB",
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 11,
            weight: 500,
          },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        min: 0,
        max: 100,
        grid: {
          color: "#F3F4F6",
          lineWidth: 1,
        },
        border: {
          display: true,
          color: "#E5E7EB",
        },
        ticks: {
          color: "#6B7280",
          font: {
            size: 12,
            weight: 500,
          },
          callback: (value) => `${value}%`,
          stepSize: 20,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  })
);

// Export test data for statistics with proper calculations
const getCurrentData = () => {
  const postData = processTestData.value;
  const preData = processPreTestData.value;

  // Return separate stats for both pre-tests and post-tests
  const postStats = {
    labels: postData.labels,
    scores: postData.scores,
    testDates: postData.testDates,
    testSubjects: postData.testSubjects,
    averageScore: 0,
    improvementPercentage: 0,
    improvement: 0,
    totalTests: postData.scores.length,
    highestScore: 0,
    lowestScore: 0,
  };

  const preStats = {
    labels: preData.labels,
    scores: preData.scores,
    testDates: preData.testDates,
    testSubjects: preData.testSubjects,
    averageScore: 0,
    improvementPercentage: 0,
    improvement: 0,
    totalTests: preData.scores.length,
    highestScore: 0,
    lowestScore: 0,
  };

  // Calculate post-test stats
  if (postData.scores.length > 0) {
    postStats.averageScore = Math.round(
      postData.scores.reduce((sum, score) => sum + score, 0) / postData.scores.length
    );

    const firstScore = postData.scores[0];
    const lastScore = postData.scores[postData.scores.length - 1];
    postStats.improvement = lastScore - firstScore;
    postStats.improvementPercentage =
      postData.scores.length >= 2 && firstScore > 0
        ? Math.round((postStats.improvement / firstScore) * 100)
        : 0;

    postStats.highestScore = Math.max(...postData.scores);
    postStats.lowestScore = Math.min(...postData.scores);
  }

  // Calculate pre-test stats
  if (preData.scores.length > 0) {
    preStats.averageScore = Math.round(
      preData.scores.reduce((sum, score) => sum + score, 0) / preData.scores.length
    );

    const firstScore = preData.scores[0];
    const lastScore = preData.scores[preData.scores.length - 1];
    preStats.improvement = lastScore - firstScore;
    preStats.improvementPercentage =
      preData.scores.length >= 2 && firstScore > 0
        ? Math.round((preStats.improvement / firstScore) * 100)
        : 0;

    preStats.highestScore = Math.max(...preData.scores);
    preStats.lowestScore = Math.min(...preData.scores);
  }

  return {
    postTests: postStats,
    preTests: preStats,
    // Legacy compatibility: return post-test data as main stats
    ...postStats,
  };
};

defineExpose({
  getCurrentData,
});
</script>

<template>
  <div class="h-96 w-full">
    <Bar
      v-if="chartData.labels && chartData.labels.length > 0"
      :data="chartData"
      :options="chartOptions"
      :key="`chart-${period}`"
    />
    <div v-else class="h-full flex items-center justify-center text-gray-400">
      <div class="text-center">
        <svg class="w-16 h-16 mx-auto mb-2 opacity-50" fill="currentColor" viewBox="0 0 24 24">
          <path d="M22,21H2V3H4V19H6V10H10V19H12V6H16V19H18V14H22V21Z" />
        </svg>
        <p class="text-sm">No test data available</p>
      </div>
    </div>
  </div>
</template>
